import { useEffect, useRef, useState } from "react";
import { Paintbrush, Loader2, Sparkles, RefreshCw, Lock } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { Button } from "./ui/button";
import { SlotCapacityNote, slotChoices } from "./ColorCountPicker";
import { RereadColoursOption, RereadConfirmDialog } from "./RereadColoursOption";
import { usd } from "../lib/money";

// Shared staff/owner repaint control. Recomputes colours from the STORED mesh (geometry/STL never change) with a
// "cleanliness" dial (ramps speckle cleanup), a colour-count upgrade, and per-swatch filament locks.
// `onRepaint(opts)` returns the updated { palette, refroze }; the page's own turntable above shows the repainted views.
// `loadReread()` resolves the opt-in "re-read colours" state ({allowed:false} hides it).
export const RepaintPanel = ({ palette = [], paintOpts, capacity, admin = false, running = false, onRepaint, loadReread }) => {
  const [clean, setClean] = useState(Math.round((paintOpts?.cleanliness ?? 0.5) * 100));
  const [colors, setColors] = useState(paintOpts?.colors || (palette.length > 12 ? 16 : palette.length > 8 ? 12 : palette.length > 4 ? 8 : 4));
  const [locks, setLocks] = useState(() => {
    const m = {};
    (paintOpts?.locks || []).forEach((l) => l.source_hex && (m[l.source_hex.toUpperCase()] = l));
    return m;
  });
  const [filaments, setFilaments] = useState([]);
  const [localBusy, setLocalBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reread, setReread] = useState(null);
  const [rereadOn, setRereadOn] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const loadRef = useRef(loadReread);
  loadRef.current = loadReread;
  const busy = localBusy || running; // `running` = the duck's server-side async repaint is in flight

  const refreshReread = () => {
    if (!loadRef.current) return;
    loadRef.current()
      .then((s) => {
        setReread(s || null);
        if (!s?.available) setRereadOn(false);
      })
      .catch(() => setReread(null));
  };

  useEffect(() => {
    axios.get(`${API}/filaments`).then(({ data }) => setFilaments(data.filaments || [])).catch(() => setFilaments([]));
  }, []);

  // re-check after every repaint: a re-read uses up this approved render's one re-read
  useEffect(() => {
    if (!running) refreshReread();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const view = preview || { palette, refroze: false };

  const setLock = (sourceHex, hex) => {
    const key = (sourceHex || "").toUpperCase();
    setLocks((prev) => {
      const next = { ...prev };
      if (!hex) delete next[key];
      else {
        const f = filaments.find((x) => x.hex === hex);
        next[key] = { source_hex: sourceHex, hex, name: f?.name, line: f?.line };
      }
      return next;
    });
  };

  const run = async (withReread = false) => {
    setLocalBusy(true);
    setError("");
    try {
      const data = await onRepaint({ colors, cleanliness: clean / 100, locks: Object.values(locks), ...(withReread ? { reread_colours: true } : {}) });
      // admin (synchronous) returns the repainted {palette, refroze}; owner (async) returns a boolean start flag
      if (data && data.palette) setPreview({ palette: data.palette || [], refroze: data.refroze });
      else if (data?.queued) setError(data.detail || "Generation is paused right now — please try again shortly.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError((Array.isArray(d) ? d[0]?.msg : d) || "Repaint failed — please try again.");
    } finally {
      setLocalBusy(false);
      if (withReread) {
        setRereadOn(false);
        refreshReread();
      }
    }
  };
  const wantsReread = rereadOn && reread?.available;
  const start = () => (wantsReread ? setConfirming(true) : run());
  const confirmReread = () => {
    setConfirming(false);
    run(true);
  };

  return (
    <div data-testid="repaint-panel" className="rounded-2xl border border-border bg-card/60 p-4 space-y-4">
      <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground">
        <Paintbrush className="h-4 w-4 text-accent" /> Fix the paint {admin ? "· auto re-freezes this order" : ""}
      </p>

      <div>
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="font-semibold">Cleanliness</span>
          <span className="font-mono2 text-muted-foreground" data-testid="repaint-cleanliness-value">{clean}%</span>
        </div>
        <input type="range" min="0" max="100" step="5" value={clean} onChange={(e) => setClean(Number(e.target.value))} disabled={busy} data-testid="repaint-cleanliness" className="w-full accent-[var(--accent)]" />
        <p className="text-xs text-muted-foreground mt-1">Higher smooths away stray triangles and speckled edges. Real details (eyes, logos) stay.</p>
      </div>

      <div>
        <span className="text-sm font-semibold">Filament slots</span>
        <div className="flex flex-wrap gap-2 mt-1.5" data-testid="repaint-colors">
          {slotChoices(paintOpts?.colors).map(({ value: c }) => (
            <button key={c} type="button" data-testid={`repaint-colors-${c}`} onClick={() => setColors(c)} disabled={busy} className={`rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${colors === c ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:border-primary"}`}>
              {c}
            </button>
          ))}
        </div>
        <SlotCapacityNote slots={colors} capacity={capacity} testid="repaint-slot-capacity-note" />
      </div>

      {view.palette.length > 0 && (
        <div data-testid="repaint-swatches">
          <span className="text-sm font-semibold flex items-center gap-1.5"><Lock className="h-3.5 w-3.5 text-muted-foreground" /> Colours — lock any to a specific spool</span>
          <ul className="mt-2 space-y-2">
            {view.palette.map((p, i) => {
              const key = (p.source_hex || p.hex || "").toUpperCase();
              const locked = locks[key];
              return (
                <li key={p.slot ?? i} className="flex items-center gap-2 text-xs">
                  <span className="h-6 w-6 rounded-full border border-border shrink-0" style={{ background: locked?.hex || p.hex }} />
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold truncate">{locked?.name || p.name}</span>
                    <span className="block font-mono2 text-muted-foreground">{Math.round((p.share || 0) * 100)}%{p.fixed ? " · duck" : ""}</span>
                  </span>
                  {!p.fixed && (
                    <select
                      data-testid={`repaint-lock-${i}`}
                      value={locked?.hex || ""}
                      onChange={(e) => setLock(p.source_hex || p.hex, e.target.value)}
                      disabled={busy || !filaments.length}
                      className="rounded-lg border border-border bg-background px-2 py-1.5 text-xs max-w-[9rem]"
                    >
                      <option value="">Auto</option>
                      {filaments.map((f) => (
                        <option key={f.hex + f.name} value={f.hex}>{f.name}</option>
                      ))}
                    </select>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <RereadColoursOption state={reread} checked={rereadOn} onChange={setRereadOn} disabled={busy} />

      <Button data-testid="repaint-run-btn" onClick={start} disabled={busy || !onRepaint} className="rounded-full gap-2 w-full font-bold">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        {busy ? "Repainting…" : wantsReread ? `Re-read colours & repaint · ~${usd(reread.cost_usd)}` : "Repaint & preview"}
      </Button>
      <RereadConfirmDialog open={confirming} state={reread} onCancel={() => setConfirming(false)} onConfirm={confirmReread} />

      {error && <p data-testid="repaint-error" className="text-xs text-rose-700 font-semibold">{error}</p>}

      {preview && (
        <p data-testid="repaint-updated-note" className="flex items-center gap-1.5 text-xs text-emerald-600 font-semibold">
          <Sparkles className="h-3.5 w-3.5" /> Updated · {view.palette.length} colours{admin ? (view.refroze ? " · print file re-freezing" : " · file not re-frozen") : ""}
        </p>
      )}

    </div>
  );
};
